import React, { useRef } from 'react';
import { ImagePlus, X, Loader2, CheckCircle2 } from 'lucide-react';

export const MAX_PHOTOS = 5;
const MAX_SIZE_MB = 10;

export function PhotoUpload({ photos, onChange, disabled }) {
  const inputRef = useRef(null);

  const addFiles = (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    const room = MAX_PHOTOS - photos.length;
    const next = list.slice(0, room).filter((f) => f.size <= MAX_SIZE_MB * 1024 * 1024).map((file) => ({
      key: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      progress: 0,
      status: 'pending',
      path: null,
    }));
    if (next.length) onChange([...photos, ...next]);
  };

  const remove = (key) => {
    const p = photos.find((x) => x.key === key);
    if (p) URL.revokeObjectURL(p.preview);
    onChange(photos.filter((x) => x.key !== key));
  };

  return (
    <div data-testid="photo-upload">
      <label className="block text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5">
        Fotos der Fläche <span className="text-slate-400 font-normal normal-case tracking-normal">(optional, max. {MAX_PHOTOS} Bilder à {MAX_SIZE_MB} MB)</span>
      </label>
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (!disabled) addFiles(e.dataTransfer.files); }}
        onClick={() => !disabled && photos.length < MAX_PHOTOS && inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed p-4 transition-colors ${photos.length < MAX_PHOTOS && !disabled ? 'cursor-pointer border-slate-300 hover:border-[#B8892A] bg-[#FAF7F2]' : 'border-slate-200 bg-slate-50'}`}
        data-testid="photo-dropzone"
      >
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="photo-input" />
        {photos.length === 0 ? (
          <div className="flex items-center gap-3 text-slate-600 text-sm">
            <div className="w-11 h-11 rounded-lg bg-[#0B1E44] text-[#E8B547] flex items-center justify-center shrink-0"><ImagePlus className="w-5 h-5" /></div>
            <div>
              <div className="font-semibold text-[#0B1E44]">Fotos hier ablegen oder klicken</div>
              <div className="text-xs text-slate-500 mt-0.5">Bilder helfen uns, Zustand und Aufwand realistisch einzuschätzen.</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" onClick={(e) => e.stopPropagation()}>
            {photos.map((p) => (
              <div key={p.key} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-white group" data-testid="photo-thumb">
                <img src={p.preview} alt="" className="w-full h-full object-cover" />
                {p.status === 'uploading' && (
                  <div className="absolute inset-0 bg-[#0B1E44]/60 flex flex-col items-center justify-center text-white text-xs font-semibold">
                    <Loader2 className="w-5 h-5 animate-spin mb-1" />{p.progress}%
                  </div>
                )}
                {p.status === 'done' && <div className="absolute bottom-1 right-1 bg-emerald-500 text-white rounded-full p-0.5"><CheckCircle2 className="w-4 h-4" /></div>}
                {p.status === 'error' && <div className="absolute inset-x-0 bottom-0 bg-red-600 text-white text-[10px] text-center py-0.5">Fehler</div>}
                {!disabled && (
                  <button type="button" onClick={() => remove(p.key)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-[#0B1E44] flex items-center justify-center shadow hover:bg-red-600 hover:text-white transition-colors" aria-label="Foto entfernen" data-testid="photo-remove">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
            {photos.length < MAX_PHOTOS && !disabled && (
              <button type="button" onClick={() => inputRef.current?.click()} className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-[#B8892A] flex items-center justify-center text-slate-400 hover:text-[#B8892A] transition-colors" data-testid="photo-add-more">
                <ImagePlus className="w-6 h-6" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
